import { IonHeader, IonPage, IonTitle } from "@ionic/react";
import { Clipboard } from "@capacitor/clipboard";
import { Share } from "@capacitor/share";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { BackButton } from "@/components/back-button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { NativeCalendarLink } from "@/components/molecules/calendar/native-calendar-link";
import { Toolbar } from "@/components/toolbar";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { buildSubscribeLinks, needsDesktopHint } from "@/lib/calendar/subscribe-links";
import { Capacitor } from "@/lib/capacitor";
import { DeviceCalendar } from "@/lib/capacitor/calendar";
import { queryClient } from "@/lib/providers/query";
import { Routes } from "@/lib/router";
import { openExternalUrl } from "@/lib/utils/open-url";
import { logger } from "@/logger";

const QUERY_KEY = ["calendar-feed", "status"] as const;

/**
 * De abonneer-URL wordt server-side nooit teruggegeven — het ruwe token bestaat maar één keer, in
 * het antwoord op "koppeling aanzetten". Daarom houden we hem alleen in component-state: navigeer
 * je weg, dan is hij weg en moet je een nieuwe genereren. Dat is bewust; een link die je later nog
 * eens kunt opvragen is een link die uit de database te halen is.
 */
function buildFeedUrl(rawToken: string): string {
  const base = import.meta.env.VITE_BACKEND_URL as string | undefined;
  return `${(base ?? "").replace(/\/$/, "")}/calendar-feed/${rawToken}.ics`;
}

export function SettingsCalendarPage() {
  const { t } = useTranslation();

  const [feedUrl, setFeedUrl] = useState<string | null>(null);

  const { data: status, isPending } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => extractionWrapper(api.calendarFeed.getStatus()),
  });

  const createMutation = useMutation({
    mutationFn: () => extractionWrapper(api.calendarFeed.createToken()),
    onSuccess: (result) => {
      setFeedUrl(buildFeedUrl(result.rawToken));
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => {
      logger.error("Calendar feed token aanmaken mislukt", { error });
      toast.error(t("settings.calendar.error.create"));
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => extractionWrapper(api.calendarFeed.revokeToken()),
    onSuccess: () => {
      setFeedUrl(null);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(t("settings.calendar.revoked"));
    },
    onError: (error) => {
      logger.error("Calendar feed intrekken mislukt", { error });
      toast.error(t("settings.calendar.error.revoke"));
    },
  });

  const isActive = status?.isActive === true;
  const isBusy = createMutation.isPending || revokeMutation.isPending;

  const platform = Capacitor.getPlatform();
  const isNativeCalendarAvailable = DeviceCalendar.isAvailable();
  const subscribeLinks = useMemo(
    () =>
      feedUrl
        ? buildSubscribeLinks({
            feedUrl,
            calendarName: t("settings.calendar.share-title"),
            platform,
          })
        : [],
    [feedUrl, platform, t],
  );

  async function copyLink() {
    if (!feedUrl) {
      return;
    }
    try {
      await Clipboard.write({ string: feedUrl });
      toast.success(t("settings.calendar.copied"));
    } catch (error) {
      logger.error("Kopiëren mislukt", { error });
    }
  }

  async function shareLink() {
    if (!feedUrl) {
      return;
    }
    try {
      await Share.share({
        title: t("settings.calendar.share-title"),
        url: feedUrl,
      });
    } catch {
      // Delen annuleren gooit ook; dat is geen fout die de gebruiker hoeft te zien.
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <Toolbar>
          <Buttons slot="secondary">
            <BackButton defaultHref={Routes.Settings()} />
          </Buttons>
          <IonTitle>{t("routes:calendar.title")}</IonTitle>
        </Toolbar>
      </IonHeader>

      <Content scrollUnderChrome>
        <div className="flex flex-1 flex-col gap-4 p-4">
          <p className="text-sm text-txt-secondary">{t("settings.calendar.intro")}</p>

          {/*
            De agenda van het toestel eerst: dat is de enige weg die op Android werkt en de enige
            die meteen bijwerkt. Het abonnement eronder blijft voor wie zijn agenda liever op een
            computer inricht. Buiten de app (web) bestaat er geen toestel-agenda, en dan is het
            abonnement niet "liever" maar het enige — vandaar dat ook de kop meebeweegt.
          */}
          {isNativeCalendarAvailable && <NativeCalendarLink hasSubscription={isActive} />}

          {isNativeCalendarAvailable && (
            <h2 className="pt-2 text-base font-semibold text-txt-primary">
              {t("settings.calendar.subscription.title")}
            </h2>
          )}

          {/*
            Verwachtingsmanagement, geen bijzin. Agenda's verversen een
            abonnement traag — soms pas na uren. Wie dat niet weet meldt het als
            bug zodra een verzette dienst niet meteen meeschuift.
          */}
          <div className="rounded-xl bg-surface-primary p-4">
            <p className="text-sm text-txt-secondary">{t("settings.calendar.not-instant")}</p>
          </div>

          {isPending ? (
            <div className="h-24 animate-pulse rounded-xl bg-neutral-50" />
          ) : feedUrl ? (
            <div className="border-brand-500/40 flex flex-col gap-3 rounded-xl border p-4">
              <div>
                <h2 className="text-base font-semibold text-txt-primary">
                  {t("settings.calendar.ready.title")}
                </h2>
                <p className="mt-1 text-sm text-txt-secondary">
                  {t("settings.calendar.ready.description")}
                </p>
              </div>

              {/*
                De aanbieders eerst. Dit is het enige moment waarop iemand de link in handen
                heeft, en één tik is de hele bedoeling van dit scherm — de rauwe URL eronder is
                de terugval, niet de route.
              */}
              <div className="flex flex-col gap-2">
                {subscribeLinks.map((link, index) => {
                  const label = t(`settings.calendar.add-to.${link.provider}`);
                  const className = [
                    "touch-target inline-flex items-center justify-center rounded-lg px-4 text-sm font-medium",
                    index === 0
                      ? "bg-brand-500 text-white"
                      : "border border-bordercolor-primary text-txt-primary",
                  ].join(" ");

                  // `webcal:` hoort door het systeem geopend te worden, niet door de in-app
                  // browser — vandaar een echte link in plaats van openExternalUrl().
                  return link.scheme === "webcal" ? (
                    <a key={link.provider} href={link.url} className={className}>
                      {label}
                    </a>
                  ) : (
                    <button
                      key={link.provider}
                      type="button"
                      onClick={() => void openExternalUrl(link.url)}
                      className={className}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {needsDesktopHint(platform) && (
                <p className="text-xs text-txt-secondary">{t("settings.calendar.desktop-only")}</p>
              )}

              <p className="text-xs text-txt-secondary">{t("settings.calendar.manual-fallback")}</p>

              <code className="block break-all rounded-lg bg-neutral-50 p-3 text-xs text-txt-primary">
                {feedUrl}
              </code>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void copyLink()}
                  className="touch-target rounded-lg border border-bordercolor-primary px-4 text-sm font-medium text-txt-primary"
                >
                  {t("settings.calendar.copy")}
                </button>
                <button
                  type="button"
                  onClick={() => void shareLink()}
                  className="touch-target rounded-lg border border-bordercolor-primary px-4 text-sm font-medium text-txt-primary"
                >
                  {t("settings.calendar.share")}
                </button>
              </div>
            </div>
          ) : isActive ? (
            <div className="flex flex-col gap-3 rounded-xl border border-bordercolor-primary p-4">
              <div>
                <h2 className="text-base font-semibold text-txt-primary">
                  {t("settings.calendar.active.title")}
                </h2>
                <p className="mt-1 text-sm text-txt-secondary">
                  {t("settings.calendar.active.description")}
                </p>
              </div>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => createMutation.mutate()}
                className="touch-target rounded-lg border border-bordercolor-primary px-4 text-sm font-medium text-txt-primary disabled:opacity-50"
              >
                {t("settings.calendar.regenerate")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={isBusy}
              onClick={() => createMutation.mutate()}
              className="touch-target rounded-lg bg-brand-500 px-4 text-sm font-medium text-white disabled:opacity-50"
            >
              {t("settings.calendar.enable")}
            </button>
          )}

          {isActive && (
            <button
              type="button"
              disabled={isBusy}
              onClick={() => revokeMutation.mutate()}
              className="touch-target rounded-lg text-sm font-medium text-error-120 disabled:opacity-50"
            >
              {t("settings.calendar.revoke")}
            </button>
          )}
        </div>
      </Content>
    </IonPage>
  );
}
